import { useCallback, useMemo } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';

import { useDecisionMakerProfilePageData } from 'src/app/pages/decisionMakerProfile/pageContext';

import { SOCIAL_PLATFORM_CONFIG, SOCIAL_PLATFORMS, type SocialLink, type SocialPlatform } from './types';
import { getSocialPlatformHostname, urlMatchesSocialPlatform } from './utils';

type Result = ModelHookResult<
	{
		headingText: string;
		personalWebsite: string | null;
		socialLinks: SocialLink[];
	},
	{
		trackView: () => void;
		handleTrackLinkClick: (link: { platform: SocialPlatform | 'personal_website'; url: string }) => void;
	}
>;

export function useElectionsData(): Result {
	const {
		data: { displayName, lastName, enrichedData },
	} = useDecisionMakerProfilePageData();
	const track = useTracking();

	const websites = useMemo(
		() => enrichedData?.websites?.map((website) => ({ label: website.label, url: website.url })) ?? [],
		[enrichedData],
	);

	// Prioritizes finding a URL with .gov domain, then any other URL that's not a social platform
	const personalWebsite = useMemo(() => {
		const nonSocialPlatforms = websites.filter(
			(w) => w.url && !SOCIAL_PLATFORMS.some((platform) => urlMatchesSocialPlatform(w.url ?? '', platform)),
		);
		const lessCommonSocialPlatforms = ['substack.com', 'tumblr.com', 'bsky.app', 'bluesky.social'];

		const nonLessCommonSocialPlatforms = nonSocialPlatforms.filter(
			(w) => !lessCommonSocialPlatforms.includes(getSocialPlatformHostname(w.url ?? '')),
		);
		const govUrl = nonLessCommonSocialPlatforms.find((w) => w.url?.includes('.gov'))?.url;
		return govUrl ?? nonLessCommonSocialPlatforms.find((w) => w.url)?.url ?? null;
	}, [websites]);

	const socialLinks = useMemo(
		() =>
			SOCIAL_PLATFORMS.flatMap((platform) => {
				const url = websites.find((w) => w.url && urlMatchesSocialPlatform(w.url, platform))?.url;
				return url ? [{ platform, url, ...SOCIAL_PLATFORM_CONFIG[platform] }] : [];
			}),
		[websites],
	);

	return {
		data: {
			headingText: `Connect with ${lastName ?? displayName}`,
			personalWebsite,
			socialLinks,
		},
		actions: {
			trackView: useCallback(() => {
				void track('dm_profile_websites_view');
			}, [track]),
			handleTrackLinkClick: useCallback(
				({ platform, url }: { platform: SocialPlatform | 'personal_website'; url: string }) => {
					void track('dm_profile_website_click', {
						media_source: platform,
						url,
					});
				},
				[track],
			),
		},
	};
}
